import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { getPreferredApiBase } from "./api/client";
import {
  getConversationMessages,
  getMyConversations,
  createGroupConversation,
  postConversationMessage,
  sendChatRequest,
  getIncomingChatRequests,
  acceptChatRequest,
  rejectChatRequest,
  uploadConversationImage,
  blockUser,
  unblockUser,
  getBlockedUsers
} from "./api/conversationsApi";
import { getUserSuggestions, getChatPeer } from "./api/usersApi";
import { removeAllFriendLinks } from "./api/matchSocialApi";
import { useAuthUser } from "./auth/AuthUserContext";
import { toSafeMediaUrl } from "./utils/safeMediaUrl";
import UserSuggestionsPanel from "./components/UserSuggestionsPanel";

/** Short time for message bubbles */
function formatChatBubbleTime(raw) {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function ChatPage() {
  const me = useAuthUser();
  const meId = typeof me?.userId === "number" ? me.userId : null;
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 900px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 900px)");
    const onChange = () => setIsNarrow(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messagesByThread, setMessagesByThread] = useState({});
  const [input, setInput] = useState("");
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [composerError, setComposerError] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [requestInfo, setRequestInfo] = useState("");
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const hubRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [groupOpen, setGroupOpen] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [groupQ, setGroupQ] = useState("");
  const [groupResults, setGroupResults] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupErr, setGroupErr] = useState("");
  const [peerProfile, setPeerProfile] = useState(null);
  const [peerLoading, setPeerLoading] = useState(false);
  const [peerError, setPeerError] = useState("");
  const [peerModalOpen, setPeerModalOpen] = useState(false);

  const activeMessages = useMemo(
    () => (activeId ? (messagesByThread[activeId] || []) : []),
    [activeId, messagesByThread]
  );
  const activeThread = useMemo(
    () => threads.find((t) => t.conversationId === activeId) || null,
    [threads, activeId]
  );

  const formatInboxError = (response) => {
    if (!response) return "Could not load conversations. Tap Retry.";
    if (response.status === 401) return "Your session expired. Please login again.";
    if (response.status === 404)
      return "Chat service is not deployed on the server yet. Deploy API updates and try again.";
    return "Could not load conversations. Tap Retry.";
  };

  const loadIncomingRequests = async () => {
    const { response, data } = await getIncomingChatRequests();
    if (!response.ok) {
      setIncomingRequests([]);
      return;
    }
    setIncomingRequests(Array.isArray(data) ? data : []);
  };

  const loadBlockedUsers = async () => {
    const { response, data } = await getBlockedUsers();
    if (!response.ok) {
      setBlockedUserIds([]);
      return;
    }
    const ids = data?.blockedUserIds;
    setBlockedUserIds(Array.isArray(ids) ? ids.map((x) => Number(x)).filter((x) => Number.isFinite(x)) : []);
  };

  useEffect(() => {
    let alive = true;
    async function loadInbox() {
      setLoading(true);
      setError("");
      try {
        const { response, data } = await getMyConversations();
        if (!response.ok) {
          setError(formatInboxError(response));
          return;
        }
        const rows = Array.isArray(data) ? data : [];
        if (!alive) return;
        setThreads(rows);
        await loadIncomingRequests();
        await loadBlockedUsers();
        if (rows.length > 0) {
          setActiveId((prev) => prev ?? rows[0].conversationId);
        }
      } catch {
        if (alive) setError("Could not load conversations. Tap Retry.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    void loadInbox();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      void loadIncomingRequests();
    }, 15000);
    return () => window.clearInterval(id);
  }, []);

  const retryInbox = async () => {
    setLoading(true);
    setError("");
    try {
      const { response, data } = await getMyConversations();
      if (!response.ok) {
        setError(formatInboxError(response));
        return;
      }
      const rows = Array.isArray(data) ? data : [];
      setThreads(rows);
      await loadIncomingRequests();
      await loadBlockedUsers();
      if (rows.length > 0) setActiveId((prev) => prev ?? rows[0].conversationId);
    } catch {
      setError("Could not load conversations. Tap Retry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!groupOpen) return;
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { response, data } = await getUserSuggestions({
          q: groupQ.trim() || undefined,
          take: 40
        });
        if (!alive) return;
        if (!response.ok) {
          setGroupResults([]);
          return;
        }
        const raw = Array.isArray(data) ? data : [];
        setGroupResults(
          raw
            .map((row) => ({
              userId: row.userId ?? row.UserId,
              username: (row.username ?? row.Username ?? "").toString()
            }))
            .filter((x) => typeof x.userId === "number" && x.username)
            .filter((x) => (meId == null ? true : x.userId !== meId))
        );
      } catch {
        if (alive) setGroupResults([]);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [groupOpen, groupQ, meId]);

  useEffect(() => {
    if (!activeId) return;
    let alive = true;
    async function loadMessages() {
      const { response, data } = await getConversationMessages(activeId, 200);
      if (!alive || !response.ok) return;
      const rows = Array.isArray(data) ? data : [];
      setMessagesByThread((prev) => ({ ...prev, [activeId]: rows }));
    }
    void loadMessages();
    return () => {
      alive = false;
    };
  }, [activeId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [activeMessages, activeId]);

  useEffect(() => {
    let mounted = true;
    async function setupHub() {
      const base = getPreferredApiBase().replace(/\/$/, "");
      const conn = new HubConnectionBuilder()
        .withUrl(`${base}/chatHub`, {
          withCredentials: true,
          headers: { "X-WillPlay-Request": "1" }
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      conn.on("ReceiveConversationMessage", (conversationId, row) => {
        if (!mounted || !conversationId || !row) return;
        setMessagesByThread((prev) => {
          const key = Number(conversationId);
          const current = prev[key] || [];
          if (current.some((m) => m.messageId === row.messageId)) return prev;
          return { ...prev, [key]: [...current, row] };
        });
        setThreads((prev) => {
          const next = prev.map((t) =>
            t.conversationId === Number(conversationId)
              ? { ...t, preview: row.message, lastMessageAtUtc: row.createdAtUtc }
              : t
          );
          return [...next].sort((a, b) => new Date(b.lastMessageAtUtc) - new Date(a.lastMessageAtUtc));
        });
      });

      conn.on("ReceiveChatRequest", (row) => {
        setIncomingRequests((prev) => {
          const reqId = Number(row?.requestId);
          if (!Number.isFinite(reqId)) return prev;
          if (prev.some((r) => Number(r.requestId) === reqId)) return prev;
          return [row, ...prev];
        });
      });

      conn.on("ChatRequestAccepted", async (payload) => {
        const conversationId = Number(payload?.conversationId);
        if (!Number.isFinite(conversationId)) return;
        setRequestInfo("Your chat request was accepted.");
        setActiveId(conversationId);
        const inbox = await getMyConversations();
        if (inbox.response.ok && Array.isArray(inbox.data)) {
          setThreads(inbox.data);
        }
        try {
          await conn.invoke("JoinConversationGroup", String(conversationId));
        } catch {
          /* ignore */
        }
      });

      conn.on("ChatRequestRejected", () => {
        setRequestInfo("Your chat request was rejected.");
      });

      try {
        await conn.start();
        const { response, data } = await getMyConversations();
        if (response.ok && Array.isArray(data)) {
          for (const t of data) {
            await conn.invoke("JoinConversationGroup", String(t.conversationId));
          }
        }
      } catch {
        // Realtime is best-effort; REST still works.
      }

      hubRef.current = conn;
    }
    void setupHub();
    return () => {
      mounted = false;
      if (hubRef.current) {
        void hubRef.current.stop();
        hubRef.current = null;
      }
    };
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if ((!text && !pendingImage) || !activeId) return;
    setComposerError("");

    let imagePayload = null;
    if (pendingImage) {
      setImageUploading(true);
      const upload = await uploadConversationImage(activeId, pendingImage);
      setImageUploading(false);
      if (!upload.response.ok) {
        setComposerError((upload.data && upload.data.message) || "Image upload failed.");
        return;
      }
      imagePayload = {
        imageUrl: upload.data?.imageUrl || null,
        imageContentType: upload.data?.imageContentType || null
      };
    }

    const { response, data } = await postConversationMessage(activeId, {
      message: text,
      imageUrl: imagePayload?.imageUrl || null,
      imageContentType: imagePayload?.imageContentType || null
    });
    if (!response.ok) {
      setComposerError((data && data.message) || "Could not send message.");
      return;
    }

    const row = data && typeof data === "object" ? data : null;
    if (row) {
      setMessagesByThread((prev) => {
        const current = prev[activeId] || [];
        if (current.some((m) => m.messageId === row.messageId)) return prev;
        return { ...prev, [activeId]: [...current, row] };
      });
    }

    setThreads((prev) =>
      [...prev]
        .map((t) => (t.conversationId === activeId ? { ...t, preview: text || "[Image]", lastMessageAtUtc: new Date().toISOString() } : t))
        .sort((a, b) => new Date(b.lastMessageAtUtc) - new Date(a.lastMessageAtUtc))
    );
    setInput("");
    setPendingImage(null);
  };

  const onPickUser = async (picked) => {
    if (!picked || typeof picked.userId !== "number") return;
    if (meId != null && picked.userId === meId) return;

    const { response, data } = await sendChatRequest(picked.userId);
    if (!response.ok) {
      setRequestInfo((data && data.message) || "Could not send chat request.");
      return;
    }
    setRequestInfo(`Chat request sent to ${picked.username}.`);
  };

  const openGroup = () => {
    setGroupErr("");
    setGroupTitle("");
    setGroupQ("");
    setGroupResults([]);
    setGroupMembers([]);
    setGroupOpen(true);
  };

  const addGroupMember = (row) => {
    if (!row || typeof row.userId !== "number") return;
    setGroupMembers((prev) => {
      if (prev.some((m) => m.userId === row.userId)) return prev;
      return [...prev, row];
    });
  };

  const removeGroupMember = (userId) => {
    setGroupMembers((prev) => prev.filter((m) => m.userId !== userId));
  };

  const createGroup = async () => {
    const title = groupTitle.trim();
    if (title.length < 2) {
      setGroupErr("Enter a group name (min 2 letters).");
      return;
    }
    if (groupMembers.length < 1) {
      setGroupErr("Pick at least 1 member.");
      return;
    }
    setGroupBusy(true);
    setGroupErr("");
    try {
      const { response, data } = await createGroupConversation(
        title,
        groupMembers.map((m) => m.userId)
      );
      if (!response.ok) {
        setGroupErr((data && data.message) || "Could not create group.");
        return;
      }
      const conversationId = Number(data?.conversationId);
      if (!Number.isFinite(conversationId)) {
        setGroupErr("Could not create group.");
        return;
      }

      setGroupOpen(false);
      setActiveId(conversationId);

      const inbox = await getMyConversations();
      if (inbox.response.ok && Array.isArray(inbox.data)) setThreads(inbox.data);

      try {
        if (hubRef.current) {
          await hubRef.current.invoke("JoinConversationGroup", String(conversationId));
        }
      } catch {
        // ignore
      }
    } finally {
      setGroupBusy(false);
    }
  };

  const acceptRequest = async (requestId) => {
    const { response, data } = await acceptChatRequest(requestId);
    if (!response.ok) {
      setRequestInfo((data && data.message) || "Could not accept request.");
      return;
    }
    const conversationId = Number(data?.conversationId);
    setIncomingRequests((prev) => prev.filter((r) => Number(r.requestId) !== Number(requestId)));
    if (Number.isFinite(conversationId)) {
      setActiveId(conversationId);
      const inbox = await getMyConversations();
      if (inbox.response.ok && Array.isArray(inbox.data)) setThreads(inbox.data);
      try {
        if (hubRef.current) {
          await hubRef.current.invoke("JoinConversationGroup", String(conversationId));
        }
      } catch {
        /* ignore */
      }
    }
  };

  const rejectRequest = async (requestId) => {
    await rejectChatRequest(requestId);
    setIncomingRequests((prev) => prev.filter((r) => Number(r.requestId) !== Number(requestId)));
  };

  const activeDirectOtherUserId = useMemo(() => {
    if (!activeThread || activeThread.conversationType !== "direct") return null;
    const raw = activeThread.otherUserId;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [activeThread]);

  const reloadPeer = useCallback(async () => {
    if (activeDirectOtherUserId == null) {
      setPeerProfile(null);
      setPeerError("");
      setPeerLoading(false);
      return;
    }
    setPeerLoading(true);
    setPeerError("");
    try {
      const { response, data } = await getChatPeer(activeDirectOtherUserId);
      if (!response.ok) {
        setPeerProfile(null);
        setPeerError("Could not load profile.");
        return;
      }
      setPeerProfile(data && typeof data === "object" ? data : null);
    } catch {
      setPeerProfile(null);
      setPeerError("Could not load profile.");
    } finally {
      setPeerLoading(false);
    }
  }, [activeDirectOtherUserId]);

  useEffect(() => {
    if (!activeThread || activeThread.conversationType !== "direct" || activeDirectOtherUserId == null) {
      setPeerProfile(null);
      setPeerError("");
      setPeerLoading(false);
      return;
    }
    void reloadPeer();
  }, [activeThread, activeDirectOtherUserId, reloadPeer]);

  const onBlock = async () => {
    if (!activeDirectOtherUserId) return;
    await blockUser(activeDirectOtherUserId);
    setBlockedUserIds((prev) => [...new Set([...prev, Number(activeDirectOtherUserId)])]);
    setRequestInfo("User blocked for incoming messages.");
    await reloadPeer();
  };

  const onUnblock = async () => {
    if (!activeDirectOtherUserId) return;
    await unblockUser(activeDirectOtherUserId);
    setBlockedUserIds((prev) => prev.filter((id) => id !== Number(activeDirectOtherUserId)));
    setRequestInfo("User unblocked.");
    await reloadPeer();
  };

  const onUnfriendAll = async () => {
    if (!activeDirectOtherUserId) return;
    const { response, data } = await removeAllFriendLinks(activeDirectOtherUserId);
    if (!response.ok) {
      setRequestInfo((data && data.message) || "Could not remove friend.");
      return;
    }
    setRequestInfo("Removed from your friends list.");
    await reloadPeer();
  };

  const isActiveUserBlocked =
    activeDirectOtherUserId != null && blockedUserIds.includes(Number(activeDirectOtherUserId));

  const blockedFromPeer =
    peerProfile != null &&
    (peerProfile.isBlockedByMe === true || peerProfile.IsBlockedByMe === true);

  const peerIsFriend = peerProfile && (peerProfile.isFriend === true || peerProfile.IsFriend === true);

  useEffect(() => {
    setPeerModalOpen(false);
  }, [activeId, activeDirectOtherUserId]);

  useEffect(() => {
    if (!peerModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPeerModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [peerModalOpen]);

  return (
    <main className="page chat-page">
      <header className="chat-page__header">
        <div>
          <h1 className="chat-page__title">Messages</h1>
          <p className="chat-page__lead">
            Request a chat → once accepted, message here. For match squads, use{" "}
            <Link to="/matches">Find matches</Link>.
          </p>
          {requestInfo ? <p className="muted small">{requestInfo}</p> : null}
        </div>
      </header>
      <div className="chat-page__layout">
        <aside className="chat-page__threads" aria-label="Conversations">
          {loading ? <p className="muted small">Loading...</p> : null}
          {error ? (
            <div style={{ padding: "0 10px 10px" }}>
              <p className="muted small" style={{ margin: "8px 0 8px" }}>
                {error}
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="ghost-btn small"
                  onClick={() => void retryInbox()}
                  disabled={loading}
                >
                  Retry
                </button>
                {String(error).toLowerCase().includes("login") ? (
                  <Link className="ghost-btn small" to="/login">
                    Go to login
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}
          <div style={{ padding: "0 10px 10px" }}>
            <button type="button" className="primary-btn small" onClick={openGroup}>
              Create group
            </button>
          </div>
          <div style={{ padding: "0 10px 10px" }}>
            <strong className="muted small">Incoming requests</strong>
            {incomingRequests.length === 0 ? (
              <p className="muted small" style={{ margin: "8px 0 0" }}>No pending requests.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gap: 8 }}>
                {incomingRequests.map((r) => (
                  <li key={r.requestId} style={{ border: "1px solid rgba(148,163,184,.35)", borderRadius: 10, padding: 8 }}>
                    <div className="muted small">{r.fromUsername} wants to chat</div>
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <button type="button" className="primary-btn small" onClick={() => void acceptRequest(r.requestId)}>Accept</button>
                      <button type="button" className="ghost-btn small" onClick={() => void rejectRequest(r.requestId)}>Reject</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <ul className="chat-thread-list">
            {threads.map((t) => (
              <li key={t.conversationId}>
                <button
                  type="button"
                  className={t.conversationId === activeId ? "chat-thread is-active" : "chat-thread"}
                  onClick={() => setActiveId(t.conversationId)}
                >
                  <div className="chat-thread__row">
                    <span className="chat-thread__title">{t.title}</span>
                    {t.conversationType === "group" ? (
                      <span className="chat-thread__badge">Group</span>
                    ) : null}
                  </div>
                  <span className="chat-thread__preview">{t.preview}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <section className="chat-page__main" aria-label="Active conversation">
          <div className="chat-panel chat-page__box">
            <header className="chat-panel__header">
              {activeThread?.conversationType === "group" ? (
                <>
                  <div className="chat-panel__title-block">
                    <h2 className="chat-panel__title">{activeThread.title || "Group"}</h2>
                    <p className="chat-panel__subtitle">Group chat</p>
                  </div>
                </>
              ) : activeThread?.conversationType === "direct" && activeId ? (
                <div className="chat-panel__title-block chat-panel__title-block--direct">
                  <button
                    type="button"
                    className="chat-panel__peer-title-btn"
                    onClick={() => setPeerModalOpen(true)}
                    disabled={peerLoading || activeDirectOtherUserId == null}
                    aria-haspopup="dialog"
                  >
                    <span className="chat-panel__title">
                      {peerLoading ? "Loading…" : peerProfile?.username ?? peerProfile?.Username ?? activeThread.title}
                    </span>
                  </button>
                  <p className="chat-panel__subtitle muted">Tap the name to see profile, stats, and actions</p>
                  {peerError ? <p className="chat-panel__peer-err">{peerError}</p> : null}
                </div>
              ) : (
                <div className="chat-panel__title-block">
                  <h2 className="chat-panel__title">Messages</h2>
                  <p className="chat-panel__subtitle muted">Choose a conversation or send a request</p>
                </div>
              )}
            </header>

            <div className="chat-page__messages" role="log" aria-live="polite">
              {activeId == null ? (
                <p className="chat-page__empty chat-page__empty--centered">
                  {isNarrow ? "Pick a conversation above or a player below." : "Select a chat or pick someone on the right."}
                </p>
              ) : activeMessages.length === 0 ? (
                <p className="chat-page__empty chat-page__empty--centered">No messages yet — say hello.</p>
              ) : (
                <div className="chat-bubbles">
                  {activeMessages.map((msg) => {
                    const sid = msg.senderUserId ?? msg.SenderUserId;
                    const isMine = meId != null && Number(sid) === meId;
                    const isGroup = activeThread?.conversationType === "group";
                    const safeImageUrl = toSafeMediaUrl(msg.imageUrl);
                    return (
                      <div
                        key={msg.messageId}
                        className={`chat-bubble-row ${isMine ? "chat-bubble-row--out" : "chat-bubble-row--in"}`}
                      >
                        <div className={`chat-bubble ${isMine ? "chat-bubble--out" : "chat-bubble--in"}`}>
                          {!isMine && isGroup ? (
                            <span className="chat-bubble__sender">{msg.senderUsername || "Member"}</span>
                          ) : null}
                          {msg.message ? <p className="chat-bubble__text">{msg.message}</p> : null}
                          {safeImageUrl ? (
                            <a className="chat-bubble__img-wrap" href={safeImageUrl} target="_blank" rel="noopener noreferrer">
                              <img src={safeImageUrl} alt="" className="chat-bubble__img" />
                            </a>
                          ) : null}
                          <time className="chat-bubble__time" dateTime={String(msg.createdAtUtc)}>
                            {formatChatBubbleTime(msg.createdAtUtc)}
                          </time>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} className="chat-bubbles__end" aria-hidden />
                </div>
              )}
            </div>

            <form className="chat-composer" onSubmit={send}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="visually-hidden-input"
                aria-hidden
                tabIndex={-1}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setPendingImage(f);
                }}
              />
              <div className="chat-composer__field">
                <input
                  type="text"
                  className="chat-composer__input"
                  placeholder={activeId == null ? "Select a chat first…" : "Message…"}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  autoComplete="off"
                  aria-label="Message text"
                  disabled={activeId == null}
                />
                <button
                  type="button"
                  className="chat-composer__attach"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={activeId == null || imageUploading}
                  aria-label="Attach image"
                  title="Attach image"
                >
                  <span className="chat-composer__attach-label">{imageUploading ? "…" : "Photo"}</span>
                </button>
              </div>
              {pendingImage ? (
                <p className="chat-composer__pending">
                  <span>{pendingImage.name}</span>
                  <button type="button" className="chat-composer__remove-pending" onClick={() => setPendingImage(null)}>
                    Remove
                  </button>
                </p>
              ) : null}
              {composerError ? <p className="chat-composer__error">{composerError}</p> : null}
              <button type="submit" className="chat-composer__send primary-btn" disabled={activeId == null}>
                Send
              </button>
            </form>
          </div>
        </section>
        <UserSuggestionsPanel variant="full" className="chat-page__people" onPickUser={onPickUser} />
      </div>

      {peerModalOpen && activeThread?.conversationType === "direct" && activeDirectOtherUserId ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-peer-modal-title"
          className="chat-peer-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPeerModalOpen(false);
          }}
        >
          <div className="chat-peer-modal">
            <button
              type="button"
              className="chat-peer-modal__close"
              onClick={() => setPeerModalOpen(false)}
              aria-label="Close profile"
            >
              ×
            </button>
            <h2 id="chat-peer-modal-title" className="chat-peer-modal__title">
              {peerLoading
                ? "Loading…"
                : peerProfile?.username ??
                  peerProfile?.Username ??
                  activeThread?.title ??
                  "Profile"}
            </h2>
            {!peerLoading && peerProfile ? (
              <>
                <p className="chat-peer-modal__handle">@{peerProfile.username ?? peerProfile.Username}</p>
                <p className="chat-peer-modal__stats">
                  Matches hosted{" "}
                  <strong>{peerProfile.matchesHosted ?? peerProfile.MatchesHosted ?? 0}</strong>
                  {" · "}
                  Matches played{" "}
                  <strong>{peerProfile.matchesPlayed ?? peerProfile.MatchesPlayed ?? 0}</strong>
                </p>
              </>
            ) : null}
            {peerError ? <p className="chat-peer-modal__err">{peerError}</p> : null}
            <div className="chat-peer-modal__actions" role="toolbar" aria-label="Profile actions">
              {blockedFromPeer || (!peerProfile && isActiveUserBlocked) ? (
                <button
                  type="button"
                  className="primary-btn small"
                  onClick={() => {
                    void onUnblock();
                    setPeerModalOpen(false);
                  }}
                  disabled={!activeDirectOtherUserId}
                >
                  Unblock
                </button>
              ) : peerProfile && peerIsFriend ? (
                <>
                  <button
                    type="button"
                    className="ghost-btn small chat-peer-modal__btn--danger"
                    onClick={() => {
                      void onUnfriendAll();
                      setPeerModalOpen(false);
                    }}
                    disabled={!activeDirectOtherUserId}
                  >
                    Unfriend
                  </button>
                  <button
                    type="button"
                    className="ghost-btn small"
                    onClick={() => {
                      void onBlock();
                      setPeerModalOpen(false);
                    }}
                    disabled={!activeDirectOtherUserId}
                  >
                    Block
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="ghost-btn small"
                  onClick={() => {
                    void onBlock();
                    setPeerModalOpen(false);
                  }}
                  disabled={!activeDirectOtherUserId}
                >
                  Block
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {groupOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2,6,23,0.65)",
            display: "grid",
            placeItems: "center",
            zIndex: 1000,
            padding: 16
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !groupBusy) setGroupOpen(false);
          }}
        >
          <div
            style={{
              width: "min(620px, 100%)",
              background: "#0b1220",
              border: "1px solid #334155",
              borderRadius: 14,
              padding: 16
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <strong>Create group chat</strong>
              <button type="button" className="ghost-btn small" disabled={groupBusy} onClick={() => setGroupOpen(false)}>
                Close
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <label className="muted small" style={{ display: "block", marginBottom: 6 }}>
                Group name
              </label>
              <input
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
                placeholder="Example: Weekend squad"
                disabled={groupBusy}
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <label className="muted small" style={{ display: "block", marginBottom: 6 }}>
                Add members
              </label>
              <input
                value={groupQ}
                onChange={(e) => setGroupQ(e.target.value)}
                placeholder="Search username..."
                disabled={groupBusy}
                style={{ width: "100%" }}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                {groupMembers.map((m) => (
                  <button
                    key={m.userId}
                    type="button"
                    className="user-suggest-panel__chip"
                    disabled={groupBusy}
                    onClick={() => removeGroupMember(m.userId)}
                    title="Remove"
                  >
                    {m.username} ×
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 10, maxHeight: 220, overflow: "auto", border: "1px solid #1e293b", borderRadius: 10 }}>
                {groupResults.length === 0 ? (
                  <p className="muted small" style={{ margin: 0, padding: 10 }}>
                    {groupQ.trim() ? "No users match." : "Type to search users."}
                  </p>
                ) : (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {groupResults.map((r) => (
                      <li key={r.userId}>
                        <button
                          type="button"
                          className="user-suggest-panel__row"
                          disabled={groupBusy}
                          onClick={() => addGroupMember(r)}
                        >
                          <span className="user-suggest-panel__name">{r.username}</span>
                          <span className="user-suggest-panel__action">Add</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {groupErr ? (
                <p className="muted small" style={{ color: "#fda4af", marginTop: 10 }}>
                  {groupErr}
                </p>
              ) : null}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
                <button type="button" className="ghost-btn small" disabled={groupBusy} onClick={() => setGroupOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="primary-btn small" disabled={groupBusy} onClick={createGroup}>
                  {groupBusy ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

export default ChatPage;
